import { useState, useEffect } from "react";
import axios from "axios";
import { Bar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
);

const MyCharts = ({ dateRange }) => {
  const [selectedOption, setSelectedOption] = useState(
    "Bookings by Property Type",
  );
  const [analytics, setAnalytics] = useState({
    bookingsbydestination: [],
    bookingsbypropertytype: [],
    revenuebydestination: [],
    monthlybookingstrend: [],
    bookingstatusbreakdown: [],
  });

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const params = new URLSearchParams();
        if (dateRange?.startDate)
          params.append("startDate", dateRange.startDate);
        if (dateRange?.endDate) params.append("endDate", dateRange.endDate);

        const url = `https://demo.zorbastays.com/web-backend/getAnalytics${
          params.toString() ? `?${params}` : ""
        }`;

        const res = await axios.get(url);
        setAnalytics(res.data || {});
      } catch (err) {
        console.error("Error fetching analytics:", err);
      }
    };

    fetchAnalytics();
  }, [dateRange]);

  // Prepare chart data dynamically
  const greenShades = [
    "#14532D", // dark green
    "#166534",
    "#15803D",
    "#16A34A",
    "#22C55E",
    "#4ADE80",
    "#86EFAC",
    "#BBF7D0",
  ];

  const chartDatasets = {
    "Bookings by Destination": {
      labels: (analytics.bookingsbydestination || []).map((b) => b.destination),
      datasets: [
        {
          data: (analytics.bookingsbydestination || []).map((b) => b.totalbookings || 0),
          backgroundColor: greenShades.slice(
            0,
            (analytics.bookingsbydestination || []).length,
          ),
        },
      ],
    },
    "Bookings by Property Type": {
      labels: (analytics.bookingsbypropertytype || []).map((b) => b.propertytype),
      datasets: [
        {
          data: (analytics.bookingsbypropertytype || []).map((b) => b.totalbookings || 0),
          backgroundColor: greenShades.slice(
            0,
            (analytics.bookingsbypropertytype || []).length,
          ),
        },
      ],
    },
    "Revenue by Destination": {
      labels: (analytics.revenuebydestination || []).map((r) => r.destination),
      datasets: [
        {
          data: (analytics.revenuebydestination || []).map((r) =>
            Number(r.totalrevenue || 0),
          ),
          backgroundColor: greenShades.slice(
            0,
            (analytics.revenuebydestination || []).length,
          ),
        },
      ],
    },
  };

  const donutOptions = {
    responsive: true,
    plugins: {
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            return tooltipItem.raw;
          },
        },
      },
      legend: { position: "right" },
    },
  };

  const [barOption, setBarOption] = useState("Monthly Bookings Trend");

  const monthLabels = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const barDatasets = {
    "Monthly Bookings Trend": {
      labels: monthLabels,
      datasets: [
        {
          label: "Bookings",
          data: monthLabels.map((_, i) => {
            const monthData = (analytics.monthlybookingstrend || []).find(
              (m) => m.month === i + 1, // backend returns month as number (1–12)
            );
            return monthData ? monthData.totalbookings || 0 : 0;
          }),
          backgroundColor: greenShades.slice(0, 12),
          borderRadius: 10,
        },
      ],
    },
    "Booking Status Breakdown": {
      labels: (analytics.bookingstatusbreakdown || []).map(
        (s) => s.bookingstatus || "Unknown", // handle null
      ),
      datasets: [
        {
          label: "Bookings",
          data: (analytics.bookingstatusbreakdown || []).map((s) => s.total || 0),
          backgroundColor: greenShades.slice(
            0,
            (analytics.bookingstatusbreakdown || []).length,
          ),
          borderRadius: 10,
        },
      ],
    },
  };

  const barOptions = {
    responsive: true,
    plugins: {
      title: {
        display: false,
        text: barOption,
      },
      legend: { position: "top" },
    },
    scales: {
      x: { grid: { display: false } },
      y: { grid: { display: false } },
    },
  };

  return (
    <div className="flex flex-col md:flex-row sm:flex-wrap justify-between mt-5 gap-5">
      {/* Donut Chart */}
      <div className="relative flex-1 p-1 h-70 rounded-lg bg-white hover:shadow-md flex justify-center items-center transition-all ease-in-out duration-300">
        <div className="absolute top-2 right-2">
          <select
            value={selectedOption}
            onChange={(e) => setSelectedOption(e.target.value)}
            className="border-gray-400 px-3 text-sm border border-gray-300/50 rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
          >
            <option>Bookings by Property Type</option>
            <option>Revenue by Destination</option>
          </select>
        </div>
        <div className="w-72 h-72">
          <Doughnut
            data={chartDatasets[selectedOption]}
            options={donutOptions}
          />
        </div>
      </div>

      {/* Bar Chart */}
      <div className="relative flex-1 p-1 min-h-64 rounded-lg bg-white hover:shadow-md flex flex-col justify-center items-center transition-all ease-in-out duration-300">
        {/* Dropdown */}
        <div className="absolute top-2 right-2">
          <select
            value={barOption}
            onChange={(e) => setBarOption(e.target.value)}
            className="border-gray-400 px-3 text-sm border border-gray-300/50 rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
          >
            <option>Monthly Bookings Trend</option>
            <option>Booking Status Breakdown</option>
          </select>
        </div>

        <div className="w-full h-80 mt-12">
          <Bar data={barDatasets[barOption]} options={barOptions} />
        </div>
      </div>
    </div>
  );
};

export default MyCharts;